//@flow
import React, {useState, useEffect} from 'react';
import {makeStyles, Typography, Grid} from '@material-ui/core';
import {colors} from '../../../assets/styles/colors';
import TrainingOverlay from './TrainingOverlay';
import WeeklyScheduleGrid from './WeeklyScheduleGrid';
import {useChatBotsActions} from '../hooks';
import {useToastActions} from '../../toast-alert/utils';

import type {StoreChatbot} from '../../../entities';

const useStyles = makeStyles(theme => ({
  sectionCard: {
    padding: '1rem',
    [theme.breakpoints.down('xs')]: {
      padding: '.5rem',
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    position: 'relative',
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  caption: {
    fontSize: '.875rem',
    color: colors.grey800,
    marginBottom: '1rem',
  },
}));

type Props = {
  chatBot: StoreChatbot,
  onSaveSuccess: () => void,
};

const ScheduleSection = React.memo(function ScheduleSection({
  chatBot,
  onSaveSuccess,
}: Props) {
  const classes = useStyles();
  const {editChatBot} = useChatBotsActions();
  const {showToast} = useToastActions();

  const [weeklySchedule, setWeeklySchedule] = useState(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (chatBot?.replySchedule) {
      setScheduleEnabled(chatBot.replySchedule.enabled || false);

      if (
        chatBot.replySchedule.ranges &&
        chatBot.replySchedule.ranges.length > 0
      ) {
        const localSchedule = convertRangesToSchedule(
          chatBot.replySchedule.ranges
        );
        setWeeklySchedule(localSchedule);
      } else {
        setWeeklySchedule(
          Array(7)
            .fill()
            .map(() => Array(24).fill(false))
        );
      }
    } else {
      setScheduleEnabled(false);
      setWeeklySchedule(
        Array(7)
          .fill()
          .map(() => Array(24).fill(false))
      );
    }
  }, [chatBot]);

  const convertScheduleToRanges = schedule => {
    if (!schedule || !scheduleEnabled) return [];

    const ranges = [];

    schedule.forEach((day, dayIndex) => {
      let rangeStart = null;

      day.forEach((isActive, slotIndex) => {
        const hourInWeek = dayIndex * 24 + slotIndex;

        if (isActive && rangeStart === null) {
          rangeStart = hourInWeek;
        } else if (!isActive && rangeStart !== null) {
          ranges.push({
            start: `${rangeStart}:00`,
            end: `${hourInWeek}:00`,
          });
          rangeStart = null;
        }
      });

      if (rangeStart !== null) {
        const endHour = (dayIndex + 1) * 24;
        ranges.push({
          start: `${rangeStart}:00`,
          end: `${endHour > 168 ? 168 : endHour}:00`,
        });
      }
    });

    return ranges;
  };

  const convertRangesToSchedule = ranges => {
    if (!ranges || !ranges.length) {
      return Array(7)
        .fill()
        .map(() => Array(24).fill(false));
    }

    const schedule = Array(7)
      .fill()
      .map(() => Array(24).fill(false));

    ranges.forEach(range => {
      const [startHour] = range.start.split(':');
      const [endHour] = range.end.split(':');

      const start = Number(startHour);
      const end = Number(endHour);

      for (let hour = start; hour < end && hour < 168; hour++) {
        const dayIndex = Math.floor(hour / 24);
        const hourIndex = hour % 24;

        if (dayIndex < 7 && hourIndex < 24) {
          schedule[dayIndex][hourIndex] = true;
        }
      }
    });

    return schedule;
  };

  const handleSaveSchedule = async () => {
    if (!chatBot?.id) return;

    try {
      setIsSaving(true);

      const replySchedule = {
        enabled: scheduleEnabled,
        ranges: scheduleEnabled ? convertScheduleToRanges(weeklySchedule) : [],
      };

      await editChatBot(chatBot.id, {
        replySchedule,
      });

      showToast({
        message: 'Horarios activos guardados correctamente',
        title: 'Cambios guardados',
        type: 'success',
        duration: 3000,
      });

      onSaveSuccess();
    } catch (error) {
      showToast({
        message: 'Error al guardar los horarios',
        title: 'Error',
        type: 'error',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid item xs={12}>
      <section className={classes.sectionCard}>
        <TrainingOverlay
          visible={isSaving}
          message="Guardando horarios activos..."
        />
        <header className={classes.sectionHeader}>
          <Typography className={classes.subtitle}>Horarios activos</Typography>
        </header>

        <Typography className={classes.caption}>
          Configura los horarios en los que tu bot estará activo para responder
          mensajes
        </Typography>

        {weeklySchedule && (
          <WeeklyScheduleGrid
            schedule={weeklySchedule}
            onScheduleChange={setWeeklySchedule}
            onSave={handleSaveSchedule}
            isSaving={isSaving}
            enabled={scheduleEnabled}
            onEnabledChange={setScheduleEnabled}
          />
        )}
      </section>
    </Grid>
  );
});

export default ScheduleSection;
